import { useState } from "react";
import { ConfirmModal } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useFcmRegistration } from "@/modules/notifications/FcmRegistrationProvider";

const DISMISSED_KEY = "a2z_notification_prompt_dismissed_at";

// Session-only suppression: as long as notifications aren't enabled, every
// fresh app open asks again. A 7-day localStorage suppression used to hide
// the prompt for a week after one tap — sellers read that as "the app never
// asks to enable notifications".
function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) !== null;
  } catch {
    return false;
  }
}

const BellIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

export function NotificationPromptBanner() {
  const t = useTranslations();
  const { status, lastError, retry, requestPermissionAndRegister } =
    useFcmRegistration();

  // 7-day suppression applies ONLY to the initial enable prompt.
  const [promptDismissed, setPromptDismissed] = useState(isDismissed);
  // Session-only suppression for the (live-status) unavailable variant.
  const [unavailableDismissed, setUnavailableDismissed] = useState(false);

  const dismissPrompt = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* private browsing */
    }
    setPromptDismissed(true);
  };

  const handleEnable = () => {
    // Provider does Notification.requestPermission() + ensureRegistered.
    void requestPermissionAndRegister();
    // Close the prompt as soon as the user decides; live status drives the
    // unavailable variant afterwards if registration can't complete.
    dismissPrompt();
  };

  // ── Initial enable prompt (permission default) ────────────────────────────
  const showPrompt = status === "prompt" && !promptDismissed;

  // ── Unavailable variant (granted, but registration can't complete) ────────
  // Shown for `offline` or transient/config `error`. Informational (NOT a
  // permission prompt) with a manual Retry. Session-dismissible only.
  const showUnavailable =
    (status === "offline" || status === "error") && !unavailableDismissed;

  const reason = lastError?.reason;
  let unavailableBody: string;
  if (status === "offline" || reason === "offline") {
    unavailableBody = t(keys.notifications.unavailableOfflineBody);
  } else if (reason === "config") {
    // Terminal — retry won't help; avoid a "retrying…" tone.
    unavailableBody = t(keys.notifications.unavailableConfigBody);
  } else {
    unavailableBody = t(keys.notifications.unavailableTransientBody);
  }

  return (
    <>
      <ConfirmModal
        tone="primary"
        icon={BellIcon}
        isOpen={showPrompt}
        onClose={dismissPrompt}
        onConfirm={handleEnable}
        title={t(keys.notifications.promptTitle)}
        subtitle={t(keys.notifications.promptSubtitle)}
        description={t(keys.notifications.promptBody)}
        confirmLabel={t(keys.notifications.promptEnable)}
        cancelLabel={t(keys.notifications.promptLater)}
        closeLabel={t(keys.common.close)}
      />

      <ConfirmModal
        tone="primary"
        icon={BellIcon}
        isOpen={showUnavailable}
        onClose={() => setUnavailableDismissed(true)}
        onConfirm={() => retry()}
        title={t(keys.notifications.unavailableTitle)}
        subtitle={t(keys.notifications.unavailableSubtitle)}
        description={unavailableBody}
        confirmLabel={t(keys.notifications.retry)}
        cancelLabel={t(keys.notifications.promptLater)}
        closeLabel={t(keys.common.close)}
      />
    </>
  );
}
